/**
 * The template's settings page — one page generated from the template
 * (SPEC-custom-agent-template.md §5), for the built-in Value Estimator and
 * for every custom agent alike.
 *
 * Sections follow the spec's switch groups: status and sponsor (§2 A, C, L),
 * the type's own switches (from BEHAVIOR_SETTINGS — the type-specific part),
 * what it reads and may produce (§2 C, D), when it runs (§2 E), where its
 * output goes (§2 F), who reviews it (§2 G), model and prompt (§2 H), a
 * manual run, learning and its record (§2 I), cost and limits (§2 K), and its
 * version (§2 L). Only a workspace administrator changes anything; others see
 * the settings as they stand. Every save goes through upsert_agent_settings.
 */

import { useCallback, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, Loader2 } from 'lucide-react';
import { PageHeader } from '../prioritization/PageHeader';
import { Callout } from '../ds';
import { AgentRunPanel } from './AgentRunPanel';
import type { AgentBatchRunner } from '../../stores/prioritization/agentRunStore';
import { useAgentSettingsStore, type AgentSettingsUpdate } from '../../stores/prioritization/agentSettingsStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { templateSettingsDefaults, type AgentDefinition, AGENT_BEHAVIORS } from '../../lib/ai/agentDefinitions';
import { hasTemplateBatchRunner, templateBatchRunnerFor } from '../../lib/agentTemplateRunners';
import { BEHAVIOR_SETTINGS } from './behaviors';
import { SwitchGroup, type SectionProps } from './template/shared';
import { StatusSection } from './template/StatusSection';
import { ReadsSection } from './template/ReadsSection';
import { TriggersSection } from './template/TriggersSection';
import { resolveTriggers } from './template/resolve';
import { DeliverySection, ReviewSection } from './template/DeliveryReviewSection';
import { ModelSection, LimitsSection } from './template/ModelLimitsSection';
import { LearningSection, LifecycleSection } from './template/LearningSection';

export interface TemplateAgentSettingsProps {
  def: AgentDefinition;
  /** Shown above the switches — a custom agent's name, description and type. */
  identity?: ReactNode;
}

export function TemplateAgentSettings({ def, identity }: TemplateAgentSettingsProps) {
  const agentType = def.agentType;
  const copy = BEHAVIOR_SETTINGS[def.behaviorKind];
  const BehaviorPanel = copy.Panel;
  const unattended = AGENT_BEHAVIORS[def.behaviorKind].runsUnattended;
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const canEdit = useUserStore((s) => s.can)('admin.settings');
  const addNotification = useUIStore((s) => s.addNotification);

  const config = useAgentSettingsStore((s) => s.byAgent[agentType]?.config ?? null);
  const isLoading = useAgentSettingsStore((s) => s.byAgent[agentType]?.isLoading ?? false);
  const isSaving = useAgentSettingsStore((s) => s.byAgent[agentType]?.isSaving ?? false);
  const error = useAgentSettingsStore((s) => s.byAgent[agentType]?.error ?? null);
  const fetchConfig = useAgentSettingsStore((s) => s.fetchConfig);
  const saveConfig = useAgentSettingsStore((s) => s.saveConfig);

  useEffect(() => {
    if (workspaceId) void fetchConfig(workspaceId, agentType);
  }, [workspaceId, agentType, fetchConfig]);

  const save = useCallback(
    async (updates: AgentSettingsUpdate, okTitle?: string) => {
      if (!workspaceId) return;
      // The first save of a template agent stores every switch group's defaults, so what this
      // page shows is what the database enforces (reviewers, delivery, limits) from the start.
      const first = !config?.behaviorKind ? { model: config?.model || def.defaultModel, ...templateSettingsDefaults(def) } : {};
      const res = await saveConfig(workspaceId, agentType, { ...first, ...updates }, userId ?? undefined);
      if (res.success && okTitle) addNotification({ type: 'success', title: okTitle, message: '', duration: 2000 });
      if (!res.success) addNotification({ type: 'error', title: 'Could not save', message: res.error ?? 'Unknown error', duration: 4000 });
    },
    [workspaceId, agentType, saveConfig, userId, addNotification, config?.behaviorKind, config?.model, def],
  );

  const batchRunner: AgentBatchRunner = useCallback(
    (wsId, scope, onProgress, signal) => templateBatchRunnerFor(def)(wsId, scope, onProgress, signal, userId),
    [def, userId],
  );

  const Icon = def.icon;
  const triggers = resolveTriggers(def, config);
  const props: SectionProps | null = workspaceId ? { def, config, workspaceId, userId: userId ?? null, isSaving, canEdit, save } : null;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Icon className={`h-4 w-4 ${def.accent.iconText}`} />}
        iconBg={def.accent.iconBg}
        title={def.name}
        subtitle={def.tagline}
      />
      <Link to="/prioritization/settings/agents" className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline">
        ← All agents
      </Link>

      {identity}

      {!workspaceId && <Callout tone="mustard">Select a workspace to configure {def.name}.</Callout>}
      {error && <Callout tone="coral">{error}</Callout>}
      {workspaceId && isLoading && !config && (
        <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      )}
      {workspaceId && !canEdit && (
        <Callout tone="neutral">Only a workspace administrator can change these settings. This is how they stand.</Callout>
      )}

      {props && (config || !isLoading) && (
        <>
          {/* A custom agent shows its description in its identity card instead. */}
          {!identity && <div className="card"><p className="text-sm text-[var(--text-sec)] leading-relaxed">{def.description}</p></div>}

          <StatusSection {...props} />

          {/* The type-specific part */}
          <BehaviorPanel agentType={agentType} settings={config} isSaving={isSaving || !canEdit} saveBehaviorConfig={(next) => save({ behaviorConfig: next })} />

          <ReadsSection {...props} docsDescription={copy.docsDescription} docsPlaceholder={copy.docsPlaceholder} />
          <TriggersSection {...props} manualSingleHelp={copy.manualSingleHelp} allEmptyLabel={copy.allEmptyLabel} />
          {/* An agent a person uses directly hands its drafts to that person: nothing to announce, no one else reviews. */}
          {unattended && <DeliverySection {...props} surfaceOnObjectHelp={copy.surfaceOnObjectHelp} />}
          {unattended && <ReviewSection {...props} />}
          {/* An agent that uses no model has no model or prompt to set. */}
          {def.evidenceModel !== 'deterministic_only' && <ModelSection {...props} />}

          {triggers.manualBulk && config?.enabled && canEdit && hasTemplateBatchRunner(def) && (
            <SwitchGroup title="Run now" lead={`${copy.runSummary(def.limits.perRunObjectCap)} ${copy.whereProposalsShow}`}
              action={<Link to={def.reportHref} className="text-xs underline text-[var(--text-sec)]">Report page</Link>}>
              <AgentRunPanel
                agentType={agentType}
                workspaceId={props.workspaceId}
                userId={userId}
                batchRunner={batchRunner}
                allEmptyLabel={copy.allEmptyLabel}
                allEmptyDescription={copy.allEmptyDescription}
                scopeTypes={def.scopeTypes}
              />
            </SwitchGroup>
          )}

          {unattended && <LearningSection {...props} learnedRulePlaceholder={copy.learnedRulePlaceholder} />}
          <LimitsSection {...props} />
          <LifecycleSection def={def} />
        </>
      )}

      {/* How it works */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-[var(--text-ter)]" />
          <p className="text-sm font-semibold text-[var(--text-sec)]">How {def.name} works</p>
        </div>
        <ul className="space-y-1.5">
          {def.howItWorks.map((line, i) => (
            <li key={i} className="text-sm text-[var(--text-sec)] leading-relaxed">• {line}</li>
          ))}
        </ul>
        <p className="text-xs text-[var(--text-ter)] pt-1">{copy.neverWrites}</p>
      </div>
    </div>
  );
}
